import restClient from "api/restClient";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import SimpleNotification from "components/Notification/SimpleNotification";
import { Action, ActionData, ActionSignature } from "./Action";

/**
 * Reusable "Test connection" record action for workspace-configuration record
 * types that expose a `POST /{resource}/{id}/test-connection` endpoint
 * (vault_config, ldap_config, devolutions_config). The endpoint performs a live
 * connectivity + authentication check against the *saved* record and returns
 * `{ ok, message, is_enabled }` with HTTP 200 even on an auth failure (ok=false),
 * so the outcome is read from the response body — never from the HTTP status. The
 * message never contains a secret value.
 * See operator-task-workspace-config-connectivity-test-action.
 */

/**
 * Workspace-configuration record types whose save/enable can be followed by an
 * automatic connectivity test (they each expose the `/{id}/test-connection`
 * route above and wire {@link makeTestConnectionActionSignature}). Keep in sync
 * with the config screens under `config/resources/*_config.tsx`.
 */
export const TEST_CONNECTION_RESOURCES = ["vault_config", "ldap_config", "devolutions_config"];

/** Workspace-configuration flag that gates the automatic post-save test. */
export const CONNECTION_TEST_ON_SAVE_FLAG = "CONNECTION_TEST_ON_SAVE_ENABLED";

/** Whether a resource type supports the automatic post-save connectivity test. */
export const supportsTestConnectionOnSave = (resourceName?: string): boolean =>
    !!resourceName && TEST_CONNECTION_RESOURCES.includes(resourceName);

/**
 * Surface the outcome of a `{ ok, message, is_enabled }` test-connection response
 * as a success/error notification. Shared by the manual action and the automatic
 * post-save runner so both read the outcome from the body, never the HTTP status.
 */
const surfaceTestConnectionResult = (result: any): void => {
    const message =
        result?.message || (result?.ok ? "Connection successful." : "Connection failed.");
    if (result?.ok) {
        NotificationService.success(
            <SimpleNotification
                title="Connection successful"
                subtitle={
                    result.is_enabled === false
                        ? `${message} (configuration is currently disabled)`
                        : message
                }
            />
        );
    } else {
        NotificationService.error(
            <SimpleNotification title="Connection failed" subtitle={message} />
        );
    }
};

export const makeTestConnectionActionSignature = ({
    resourceName,
    label = "Test connection",
}: {
    resourceName: string;
    label?: string;
}): ActionSignature => {
    const action: Action = (data: ActionData) => {
        const id = data.resourceData?.id;
        if (!id) {
            NotificationService.error(
                <SimpleNotification
                    title="Cannot test connection"
                    subtitle="Please save the configuration first."
                />
            );
            return Promise.resolve(null);
        }
        return restClient
            .post(`/${resourceName}/${id}/test-connection`)
            .then((response) => {
                surfaceTestConnectionResult(response?.data ?? {});
                // Settle (resolve) — the outcome was already surfaced above.
                return response;
            })
            .catch((error) => {
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride="Could not test connection"
                        error={error}
                    />
                );
                // Resolve (not reject) so the action framework does not raise a
                // second, generic error on top of the specific one shown above.
                return null;
            });
    };

    return {
        key: `test-connection-${resourceName}`,
        action,
        label: () => label,
        icon: () => IconCollection.connection,
        // Hide on the create screen, where there is no saved record to test.
        omitWhen: (data: ActionData) => !data.resourceData?.id,
    };
};

/**
 * Automatically run the connectivity test after a config record was saved/enabled
 * via the frontend (gated on {@link CONNECTION_TEST_ON_SAVE_FLAG}). Makes the
 * phases visible to the acting user — a persistent "Testing connection…" toast is
 * shown while the (potentially slow) live check runs, then replaced by the
 * success/failed outcome — so a bad credential or unreachable server surfaces
 * immediately instead of silently at first use.
 *
 * Fire-and-forget: never rejects, so it cannot disrupt the save's own promise
 * chain. Callers should NOT await it.
 */
export const runTestConnectionAfterSave = (resourceName?: string, id?: string): void => {
    if (!supportsTestConnectionOnSave(resourceName) || !id) {
        return;
    }
    const testingToastId = NotificationService.info(
        <SimpleNotification
            title="Testing connection…"
            subtitle="Verifying connectivity and authentication with the saved configuration."
        />,
        { autoClose: false }
    );
    restClient
        .post(`/${resourceName}/${id}/test-connection`)
        .then((response) => {
            surfaceTestConnectionResult(response?.data ?? {});
        })
        .catch((error) => {
            NotificationService.error(
                <RestErrorNotification
                    titleOverride="Could not test connection"
                    error={error}
                />
            );
        })
        .finally(() => {
            NotificationService.dismiss(testingToastId);
        });
};
